import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Image } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import TestModeToggle from '../TestMode/TestModeToggle';
import PortalShellContext from './PortalShellContext';
import LessonReturnPill from './LessonReturnPill';
import NotificationBell from './NotificationBell';
import LessonNudges from './LessonNudges/LessonNudges';
import ThemePicker from './ThemePicker';
import { usePortalTheme } from './usePortalTheme';
import { useChromeThemeColor } from './useChromeThemeColor';
import { usePageCanvasColor } from './usePageCanvasColor';
import { PORTAL_HOME, visibleNavGroups } from './portalNav';
import { hasPerm } from '../../utils/permissions';
import SpaceSwitcher from './SpaceSwitcher';
import { SPACES, getReachableSpaces } from './portalSpaces';
import logoShort from '../../assets/LOGOshort.png';
import './PortalLayout.css';
import GuideTour from './Guide/GuideTour';
// Comfort-theme scoped button/focus overrides (additive; no-op at Default).
import '../../styles/themes/comfort-portal.css';
/* SpaceSwitcher's own styles (.psp-switcher*). PortalSpaceChrome already
   imports this, but Work's admin chrome is PortalLayout and never did — so the
   switcher would render unstyled here without it. Imported explicitly rather
   than leaning on another component having loaded it first. */
import '../../styles/portal/portalSpaces.css';

// Renders the grouped nav links. Shared by the persistent desktop sidebar and
// the mobile drawer; onNavigate lets the drawer close itself on tap.
// `collapsed` is the icon-rail state: labels are hidden in CSS (not unmounted,
// so the text still reaches assistive tech and nothing reflows on toggle), and
// a native `title` supplies the hover tooltip that replaces the visible label.
// The drawer never passes it — the mobile presentation is always full width.
function PortalNavLinks({ groups, onNavigate, collapsed = false }) {
	const label = (text) => (collapsed ? text : undefined);
	return (
		<nav className='portal-nav'>
			<NavLink
				to={PORTAL_HOME.to}
				end={PORTAL_HOME.end}
				onClick={onNavigate}
				title={label(PORTAL_HOME.label)}
				className={({ isActive }) =>
					`portal-nav__item${isActive ? ' is-active' : ''}`
				}>
				<i className={`fa-sharp fa-solid ${PORTAL_HOME.icon}`} />
				<span>{PORTAL_HOME.label}</span>
			</NavLink>

			{groups.map((group) => (
				<div className='portal-nav__group' key={group.title}>
					<div className='portal-nav__group-title'>
						<i className={`fa-sharp fa-solid ${group.icon}`} />
						<span>{group.title}</span>
					</div>
					{group.items.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							data-guide={`nav-${item.to.split('/').pop()}`}
							onClick={onNavigate}
							title={label(item.label)}
							className={({ isActive }) =>
								`portal-nav__item${isActive ? ' is-active' : ''}`
							}>
							<i className={`fa-sharp fa-solid ${item.icon}`} />
							<span>{item.label}</span>
						</NavLink>
					))}
				</div>
			))}
		</nav>
	);
}

// Collapsed-sidebar preference. Read once, lazily, in the useState initializer
// so the rail paints collapsed on the FIRST frame — reading it in an effect
// would flash the full sidebar and then snap narrow on every page load.
// Wrapped because localStorage throws outright in Safari private mode, and a
// nav preference is never worth taking the shell down for.
const SIDEBAR_COLLAPSED_KEY = 'cyv:portal:sidebarCollapsed';

function readCollapsedPref() {
	try {
		return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
	} catch {
		return false;
	}
}

function PortalLayout() {
	const { currentUser, logout, userRole, userProfile } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPref);
	const [isStandalone, setIsStandalone] = useState(false);
	const drawerRef = useRef(null);
	// Mirrors the rules wall client-side; owners get all four, everyone else Work.
	const reachableSpaces = getReachableSpaces(userProfile, currentUser?.email);
	// Opt-in, portal-scoped theme. appliedTheme is null for 'default', which
	// omits the data-theme attribute entirely → untouched look.
	const { selection: themeSelection, setSelection: setThemeSelection, appliedTheme } =
		usePortalTheme();
	// Keep the installed-PWA titlebar on the same colour as the chrome bar.
	const shellRef = useRef(null);
	useChromeThemeColor(shellRef, appliedTheme);
	usePageCanvasColor(shellRef, appliedTheme);

	// Gating lives in portalNav.visibleNavGroups so it can be unit-tested — see
	// its header for why (an inline filter here was unreachable by any test).
	const groups = visibleNavGroups(userProfile, hasPerm);

	useEffect(() => {
		const standalone =
			window.matchMedia('(display-mode: standalone)').matches ||
			window.navigator.standalone === true;
		setIsStandalone(standalone);
	}, []);

	// Sync React state → wa-drawer.open property (matches the main-nav pattern).
	useEffect(() => {
		const drawer = drawerRef.current;
		if (drawer) drawer.open = drawerOpen;
	}, [drawerOpen]);

	// Reflect drawer dismissals (esc, backdrop, close button) back into state.
	useEffect(() => {
		const drawer = drawerRef.current;
		if (!drawer) return;
		const handleHide = () => setDrawerOpen(false);
		drawer.addEventListener('wa-after-hide', handleHide);
		return () => drawer.removeEventListener('wa-after-hide', handleHide);
	}, []);

	// Close the drawer whenever the route changes.
	useEffect(() => {
		setDrawerOpen(false);
	}, [location.pathname]);

	// Persist the rail state. Deliberately NOT in the click handler: this way the
	// stored value follows the state whatever sets it, and a storage failure can
	// never block the toggle itself from working.
	useEffect(() => {
		try {
			window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
		} catch {
			/* private mode — the preference just doesn't survive the session */
		}
	}, [sidebarCollapsed]);

	const handleLogout = async () => {
		try {
			await logout();
			navigate('/portal/login');
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	const roleLabel =
		userRole === 'admin'
			? 'Admin'
			: userRole === 'employee'
			? 'Employee'
			: 'Customer';

	const userMenu = (
		<Dropdown align='end'>
			<Dropdown.Toggle variant='outline-secondary' id='portal-user-dropdown'>
				<i className='fa-sharp fa-solid fa-user' />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Header>
					<small>{currentUser?.email}</small>
					<div>
						<small className='badge bg-primary mt-1'>{roleLabel}</small>
					</div>
				</Dropdown.Header>
				<Dropdown.Divider />
				<Dropdown.Item onClick={handleLogout}>
					<i className='fa-sharp fa-solid fa-right-from-bracket me-2' />
					Log Out
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	return (
		<PortalShellContext.Provider value={true}>
			<div
				ref={shellRef}
				className='cyv portal-shell'
				data-theme={appliedTheme || undefined}>
				{/* Persistent sidebar (desktop + tablet; mobile uses the drawer below).
				    Collapsing narrows it to an icon rail rather than hiding it: the
				    nav stays one click away and the toggle stays anchored in the
				    sidebar instead of becoming a button floating over page content. */}
				<aside
					id='portal-sidebar'
					className={`portal-sidebar d-none d-md-flex${
						sidebarCollapsed ? ' is-collapsed' : ''
					}`}>
					<div className='portal-sidebar__head'>
						<Link
							to='/portal/work'
							className='portal-sidebar__brand'
							onClick={() => window.scrollTo(0, 0)}>
							<Image src={logoShort} alt='Color Your Voice' />
							<span>Employee Portal</span>
						</Link>
						<button
							type='button'
							className='portal-sidebar__collapse'
							aria-expanded={!sidebarCollapsed}
							aria-controls='portal-sidebar'
							aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
							title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
							onClick={() => setSidebarCollapsed((v) => !v)}>
							<i
								className={`fa-sharp fa-solid ${
									sidebarCollapsed ? 'fa-angles-right' : 'fa-angles-left'
								}`}
							/>
						</button>
					</div>
					<div className='portal-sidebar__scroll'>
						<PortalNavLinks groups={groups} collapsed={sidebarCollapsed} />
					</div>
				</aside>

				{/* Main column: top bar + page content */}
				<div className='portal-main'>
					<header className='portal-topbar'>
						<button
							type='button'
							className='portal-burger d-md-none'
							aria-label='Open menu'
							aria-expanded={drawerOpen}
							onClick={() => setDrawerOpen(true)}>
							<i className='fa-sharp fa-solid fa-bars' />
						</button>

						<Link
							to='/portal/work'
							className='portal-topbar__brand d-md-none'
							onClick={() => window.scrollTo(0, 0)}>
							<Image src={logoShort} alt='Color Your Voice' />
						</Link>

						{/* The way OUT of Work. Personal/Owners/Barbara have carried a
						    switcher (and its "Back to hub") since Phase B via
						    PortalSpaceChrome, but Work's admin chrome is PortalLayout,
						    which never mounted one — so Work was the one space with no
						    exit except the browser's back button, and a standalone PWA
						    has no back button (Anthony, 2026-08-03).

						    Same component, not a bespoke button, so the four spaces keep
						    one switching affordance. It self-hides for a single-space
						    user by its own contract — non-owners reach only Work
						    (getReachableSpaceKeys), so a teacher sees nothing new. */}
						{reachableSpaces.length > 1 && (
							<SpaceSwitcher
								spaces={reachableSpaces}
								current={SPACES.work}
							/>
						)}

						{/* The way BACK INTO an open lesson. Leaving a lesson is now
						    non-destructive (LessonSessionContext + useLessonScratch);
						    this is what makes it findable again — the installed PWA has
						    no browser back button, the same gap that put the space
						    switcher above here. Self-hides unless a lesson is open and
						    you are somewhere else. */}
						<LessonReturnPill />

						<div className='portal-topbar__spacer' />

						<div className='portal-topbar__actions'>
							{(userRole === 'employee' || userRole === 'admin') && (
								<TestModeToggle />
							)}
							<ThemePicker
								value={themeSelection}
								onChange={setThemeSelection}
							/>
							{isStandalone && (
								<span className='portal-appmode-badge'>
									<i className='fa-sharp fa-solid fa-mobile-screen' />
									App Mode
								</span>
							)}
							{/* lg, not md: at 768 the sidebar (264px) leaves the bar 504px,
							    and this 176px block is the dropdown header's own content —
							    email + role — shown twice. Measured 2026-09-23. */}
							<div className='portal-topbar__user d-none d-lg-block'>
								<small className='text-muted d-block'>
									{currentUser?.email}
								</small>
								<small className='badge bg-primary'>{roleLabel}</small>
							</div>
							<NotificationBell />
							{userMenu}
						</div>
					</header>

					<main className='portal-content'>
						<Outlet />
					</main>
					{/* Stays up until each of the teacher's late or unmarked lessons
					    has an answer on the record; hidden on the lesson page itself. */}
					<LessonNudges />
					<GuideTour />
				</div>

				{/* Mobile drawer (navy treatment, matches the main-nav drawer) */}
				<wa-drawer
					ref={drawerRef}
					label='Employee Portal'
					placement='start'
					id='portal-drawer'
					className='portal-drawer'>
					<PortalNavLinks
						groups={groups}
						onNavigate={() => setDrawerOpen(false)}
					/>
				</wa-drawer>
			</div>
		</PortalShellContext.Provider>
	);
}

export default PortalLayout;
